"use client";

import { useMotionValue } from "framer-motion";
import {
  Asterisk,
  Bank,
  Bell,
  Book,
  Calendar,
  ChatLines,
  CheckCircle,
  CreditCards,
  Headset,
  HelpCircle,
  List,
  MultiplePages,
  NavArrowDown,
  NavArrowRight,
  OpenNewWindow,
  Presentation,
  Search,
  SnowFlake,
  Wallet,
  Xmark,
} from "iconoir-react";
import { useLocale } from "next-intl";
import * as React from "react";

import {
  AppBank,
  AppCards,
  AppConnections,
  AppDocuments,
  AppFinancing,
  AppGetStarted,
  AppOverview,
  AppPerformance,
  AppTransactions,
} from "@/components/home/nav-icons";
import { FadeLine } from "@/components/ui/fade-line";
import { GridCross, GridElbow } from "@/components/ui/grid-connector";
import { StepTabs } from "@/components/ui/step-tabs";
import { localizeCurrencyString } from "@/lib/localize-currency";
import { cn } from "@/lib/utils";

import { PRODUCT_PREVIEW_STEPS } from "./product-preview-steps";
import { ProductPreviewVideoPanel } from "./product-preview-video-panel";
import type { ProductPreviewVideoPanelHandle } from "./product-preview-video-panel";
import type { ProductPreviewVideo } from "./product-preview-videos";
import {
  getProductPreviewDesktopVideos,
  getProductPreviewMobileVideos,
  PRODUCT_PREVIEW_MOBILE_SM_TAB_CROPS,
} from "./product-preview-videos";

interface ProductPreviewProps {
  className?: string;
  steps?: readonly string[];
  /** Extra content absolutely positioned over the desktop card (e.g. review bubbles). */
  desktopOverlay?: React.ReactNode;
}

// Figma "Hero Product" (node 4890:13718) — the application screen mock that
// sits directly under the hero. Three onboarding step tabs over a scaled-down
// Wayflyer app showing the "Get approved for financing" flow.
const DEFAULT_STEPS = PRODUCT_PREVIEW_STEPS;

// Real Sanity CDN headshots (allowed image domain) to match the Figma photo
// avatars in the top bar and the account-manager card.
const TOPBAR_AVATAR =
  "https://cdn.sanity.io/images/1aendizt/localized-production/9a4d44553d64a6a03e08242e4b61f87760bd36e8-800x800.jpg?w=48&h=48&fit=crop&auto=format";
const MANAGER_AVATAR =
  "https://cdn.sanity.io/images/1aendizt/redesign/9cf02a315f2ebff3b6a0223ef48e278243695c5f-49x49.png";

export function ProductPreview({
  className,
  steps = DEFAULT_STEPS,
  desktopOverlay,
}: ProductPreviewProps) {
  const locale = useLocale();
  const stepLabels = steps;
  const [active, setActive] = React.useState(0);
  const [replayKey, setReplayKey] = React.useState(0);
  const progress = useMotionValue(0);
  const reducedMotion = usePrefersReducedMotion();
  const isDesktopLayout = useIsDesktopProductPreview();
  const desktopVideos = React.useMemo(
    () => getProductPreviewDesktopVideos(locale),
    [locale]
  );
  const mobilePanelVideoConfig = useMobilePanelVideoConfig(locale);
  const mobilePanelRef = React.useRef<ProductPreviewVideoPanelHandle>(null);
  const desktopPanelRef = React.useRef<ProductPreviewVideoPanelHandle>(null);

  const playVisibleTab = React.useCallback(
    (index: number) => {
      if (isDesktopLayout) {
        desktopPanelRef.current?.playTab(index);
        return;
      }
      mobilePanelRef.current?.playTab(index);
    },
    [isDesktopLayout]
  );

  const selectTab = React.useCallback(
    (index: number) => {
      setActive(index);
      setReplayKey((key) => key + 1);
      progress.set(0);
      // Play synchronously inside the click handler so the browser keeps
      // user activation — deferred useLayoutEffect play() can be blocked.
      playVisibleTab(index);
    },
    [playVisibleTab, progress]
  );

  const advanceTab = React.useCallback(() => {
    setActive((current) => (current + 1) % stepLabels.length);
    progress.set(0);
  }, [progress, stepLabels.length]);

  return (
    <section
      className={cn(
        // Mobile (Figma 6115:32319 / 10311:41395): 24px sides + bottom on the
        // gradient shelf; top padding removed — hero text supplies the 32px gap
        // above (WEB-440). Desktop keeps a flat grey shelf.
        "relative bg-gradient-to-b from-[#f9f9f9] to-[#e8e8e8] px-6 pt-0 pb-6 lg:bg-[#f9f9f9] lg:bg-none lg:px-5 lg:py-0",
        className
      )}
    >
      {/* Mobile (Figma 6115:32319): a plain bordered card — the tab bar over the
          clipped app, with a grid connector at ALL FOUR corners. No gradient
          shelf, hairline or glow (those are desktop-only). */}
      <div className="relative mx-auto w-full lg:hidden">
        <GridCross
          corner="top-left"
          className="absolute -top-[0.5px] -left-[0.5px] z-30"
        />
        <GridCross
          corner="top-right"
          className="absolute -top-[0.5px] -right-[0.5px] z-30"
        />
        <GridCross
          corner="bottom-left"
          className="absolute -bottom-[0.5px] -left-[0.5px] z-30"
        />
        <GridCross
          corner="bottom-right"
          className="absolute -right-[0.5px] -bottom-[0.5px] z-30"
        />

        <StepTabs
          tabs={stepLabels.map((label, index) => ({
            id: String(index),
            label,
          }))}
          active={active}
          progress={progress}
          onSelect={selectTab}
          ariaLabel="Onboarding steps"
          hideLeftBorderOnDesktop
          collapseInactiveOnMobile
        />
        {/* Frame 6115:32336 — tab content area; @container drives mobile crop. */}
        <div className="@container relative h-[419px] overflow-hidden border border-t-0 border-[#e5e6e6] bg-[#fbfbfb]">
          <ProductPreviewVideoPanel
            ref={mobilePanelRef}
            active={active}
            replayKey={replayKey}
            progress={progress}
            reducedMotion={reducedMotion}
            playbackEnabled={!isDesktopLayout}
            onAdvance={advanceTab}
            videos={mobilePanelVideoConfig.videos}
            videoCrop={mobilePanelVideoConfig.videoCrop}
            tabObjectPositions={mobilePanelVideoConfig.tabObjectPositions}
            tabScales={mobilePanelVideoConfig.tabScales}
            className="size-full border-b-0"
          />
        </div>
      </div>

      {/* Desktop — Figma gradient card (5663:23079): #F9F9F9 → #E8E8E8, rounded
          16px, ~20px side margins. The gradient pools into a ~96px bottom shelf;
          the 1064 content frame carries three Direction corner marks on the
          content rail (Figma 5663:23125 / 23126 / 23127) plus a top-left
          GridCross on the tab bar (5663:23118). */}
      <div className="relative mx-auto hidden max-w-[1398px] rounded-2xl lg:block">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl bg-gradient-to-b from-[#f9f9f9] to-[#e8e8e8]"
        />
        <div className="relative pb-24">
          <FadeLine className="top-0 right-0 left-0" />

          {/* Ground shadow (Figma Ellipse 2, 5663:23082) — a #C2C2C2 ellipse with
              a ~52px layer blur straddling the app's bottom edge, BEHIND the UI.
              It darkens the gradient shelf around the visual so the backdrop
              reads as a frame around the white app. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-[87px] mx-auto h-[105px] w-[1063px] max-w-[76%] rounded-[100%] bg-[#c2c2c2] blur-[52px]"
          />

          <div className="relative mx-auto w-full max-w-[1064px] overflow-visible">
            {/* 10px rim (Figma 5663:23083's inside stroke): 25% white extending
                beyond the visual's sides and bottom. Over the card's top it adds
                nothing visible (#f9f9f9 + white/25 ≈ #fafafa); lower down it
                reads as a band slightly lighter than the gradient, and under the
                visual it lets the ground shadow glow through (238 sides / 224
                centre, matching the design render). */}
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-x-[10px] top-0 -bottom-[10px] bg-white/25"
            />
            {/* Figma 5663:23079 — three 4×4 Direction corners on the content
                rail (23125 top-right, 23126 bottom-right, 23127 bottom-left)
                plus a GridCross at the tab bar top-left (5663:23118). */}
            <GridElbow
              corner="top-right"
              className="text-grey-950 pointer-events-none absolute -top-[0.5px] -right-[0.5px] z-30"
            />
            <GridElbow
              corner="bottom-left"
              className="text-grey-950 pointer-events-none absolute -bottom-[0.5px] -left-[0.5px] z-30"
            />
            <GridElbow
              corner="bottom-right"
              className="text-grey-950 pointer-events-none absolute -right-[0.5px] -bottom-[0.5px] z-30"
            />

            {/* Side borders (Figma Lines 520/521) — 1px vertical fade lines along
                the visual's edges, spanning tabs + app from the card's top
                hairline down 665px: transparent at the top, solid through the
                middle, fading out ~20px above the app's bottom edge. */}
            <FadeLine
              orientation="vertical"
              className="top-0 left-0 z-20 h-[665px]"
            />
            <FadeLine
              orientation="vertical"
              className="top-0 right-0 z-20 h-[665px]"
            />

            <StepTabs
              tabs={stepLabels.map((label, index) => ({
                id: String(index),
                label,
              }))}
              active={active}
              progress={progress}
              onSelect={selectTab}
              ariaLabel="Onboarding steps"
              hideLeftBorderOnDesktop
              collapseInactiveOnMobile
            />
            <ProductPreviewVideoPanel
              ref={desktopPanelRef}
              active={active}
              replayKey={replayKey}
              progress={progress}
              reducedMotion={reducedMotion}
              playbackEnabled={isDesktopLayout}
              onAdvance={advanceTab}
              videos={desktopVideos}
            />
          </div>

          {desktopOverlay}
        </div>
      </div>
    </section>
  );
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return reduced;
}

/** md+ in the mobile card frame uses desktop hero clips; sm uses mobile clips (WEB-738). */
type MobilePanelVideoConfig = {
  videos: readonly ProductPreviewVideo[];
  videoCrop: "mobile" | "desktop";
  tabObjectPositions?: readonly (string | undefined)[];
  tabScales?: readonly (number | undefined)[];
};

const MOBILE_SM_TAB_OBJECT_POSITIONS = PRODUCT_PREVIEW_MOBILE_SM_TAB_CROPS.map(
  (crop) => crop.objectPosition
);
const MOBILE_SM_TAB_SCALES = PRODUCT_PREVIEW_MOBILE_SM_TAB_CROPS.map(
  (crop) => crop.scale
);

function useMobilePanelVideoConfig(locale: string): MobilePanelVideoConfig {
  const desktopVideos = React.useMemo(
    () => getProductPreviewDesktopVideos(locale),
    [locale]
  );
  const mobileVideos = React.useMemo(
    () => getProductPreviewMobileVideos(locale),
    [locale]
  );
  const [config, setConfig] = React.useState<MobilePanelVideoConfig>({
    videos: mobileVideos,
    videoCrop: "mobile",
    tabObjectPositions: MOBILE_SM_TAB_OBJECT_POSITIONS,
    tabScales: MOBILE_SM_TAB_SCALES,
  });

  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 624px)");

    const sync = () => {
      if (mq.matches) {
        setConfig({
          videos: desktopVideos,
          videoCrop: "desktop",
          tabObjectPositions: undefined,
          tabScales: undefined,
        });
        return;
      }

      setConfig({
        videos: mobileVideos,
        videoCrop: "mobile",
        tabObjectPositions: MOBILE_SM_TAB_OBJECT_POSITIONS,
        tabScales: MOBILE_SM_TAB_SCALES,
      });
    };

    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [desktopVideos, mobileVideos]);

  return config;
}

/** Matches the mobile/desktop product-preview layout split (Tailwind lg = 1056px). */
function useIsDesktopProductPreview() {
  const [isDesktop, setIsDesktop] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(min-width: 1056px)").matches;
  });

  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 1056px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return isDesktop;
}

function BusinessDetailsPanel({ headline }: { headline: string }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <h3 className="text-[24px] leading-none font-medium tracking-[-0.2976px] whitespace-nowrap text-[#0d0d0d]">
          {headline}
        </h3>
        <p className="text-[12px] leading-[1.4] whitespace-nowrap text-[#737373]">
          We&rsquo;ll use your business details to find your information
          automatically.
        </p>
      </div>

      <div className="flex max-w-[317px] flex-col gap-[26px]">
        <div className="flex flex-col gap-[13px]">
          <Field label="Legal name" value="Nörd Inc." clearable />

          <div className="flex flex-col gap-[5px]">
            <DataRow
              label="Address"
              value="411 Mangrove Street, Nevada, 89101, US"
            />
            <DataRow label="Status" value="Active" />
            <DataRow label="Identifier" value="85-45698002" />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <Field label="Business type" value="Private Limited Company" />
          <Field
            label="Date of incorporation"
            value="08/10/2014"
            icon={<Calendar className="size-3.5" strokeWidth={1.6} />}
          />
          <div className="flex flex-col gap-[13px]">
            <Field label="Industry" value="Apparel" />
            <Field
              label="Australian Business Register (ABN)"
              value="48 328 415 908"
              muted
            />
            <Field
              label="Registered address"
              value="Aston House, Cornwall Avenue, London, N3 1LF"
              muted
            />

            <div className="flex flex-col gap-[5px]">
              <span className="text-[9px] leading-none font-medium text-[#0d0d0d]">
                Website
              </span>
              <span className="flex h-[26px] items-center gap-[5px] rounded-[5px] border-[0.6px] border-[#676765] bg-white px-[10px]">
                <span className="text-[10px] text-[#676765]">http://</span>
                <span className="flex-1 truncate text-[10px] text-[#0d0d0d]">
                  www.nord.fit
                </span>
              </span>
              <span className="text-[8px] text-[#676765]">
                We prefer your shop URL over your corporate site
              </span>
            </div>

            <div className="flex items-center gap-[5px]">
              <span
                aria-hidden
                className="size-[11px] shrink-0 rounded-[3px] border-[0.6px] border-[#676765] bg-white"
              />
              <span className="text-[9px] text-[#0d0d0d]">
                I don&rsquo;t have a website
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConnectPlatformsPlaceholder() {
  const platforms = [
    { name: "Shopify", url: "nord.myshopify.com", connected: true },
    { name: "Amazon", url: "nord.amazon.com", connected: true },
    { name: "Stripe", url: "Connect Stripe", connected: false },
  ];

  return (
    <div className="flex max-w-[360px] flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-[24px] leading-none font-medium tracking-[-0.2976px] text-[#0d0d0d]">
          Connect your platforms
        </h3>
        <p className="text-[12px] leading-[1.4] text-[#737373]">
          Securely link your sales channels so we can assess performance in
          minutes.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {platforms.map((platform) => (
          <div
            key={platform.name}
            className="flex items-center justify-between rounded-lg border border-[#e6e6e5] bg-white px-3 py-2.5"
          >
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-[#0d0d0d]">
                {platform.name}
              </p>
              <p className="truncate text-[9px] text-[#737373]">
                {platform.url}
              </p>
            </div>
            {platform.connected ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#e8f5e9] px-2 py-0.5 text-[9px] font-medium text-[#1b7f3d]">
                <CheckCircle className="size-2.5" strokeWidth={2} aria-hidden />
                Connected
              </span>
            ) : (
              <span className="rounded-full border border-[#001eff] px-2 py-0.5 text-[9px] font-medium text-[#001eff]">
                Connect
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function GetApprovedPlaceholder() {
  const locale = useLocale();

  return (
    <div className="flex max-w-[360px] flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-[24px] leading-none font-medium tracking-[-0.2976px] text-[#0d0d0d]">
          Get approved for financing
        </h3>
        <p className="text-[12px] leading-[1.4] text-[#737373]">
          Your application is under review. We&rsquo;ll notify you as soon as a
          decision is ready.
        </p>
      </div>

      <div className="rounded-xl border border-[#e6e6e5] bg-white p-4 shadow-[0px_4px_1px_-2px_rgba(0,0,0,0.04)]">
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-medium text-[#333333]">
            Financing offer
          </span>
          <span className="rounded bg-[#e4edff] px-1 py-0.5 text-[11px] font-medium text-[#000c66]">
            In review
          </span>
        </div>
        <p className="mt-3 text-[20px] font-medium tracking-[-0.02em] text-[#0d0d0d]">
          {localizeCurrencyString("Up to $250,000", locale)}
        </p>
        <p className="mt-1 text-[11px] text-[#737373]">
          Based on your connected platform data
        </p>
      </div>
    </div>
  );
}

/** Shared Wayflyer app chrome — sidebar + top bar wrapping arbitrary panel content. */
export function AppMockShell({
  activeNavLabel,
  children,
  className,
  compact,
}: {
  activeNavLabel?: string;
  children: React.ReactNode;
  className?: string;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "relative grid h-[623px] overflow-hidden bg-[#fbfbfb]",
        compact
          ? "grid-cols-[43px_1fr]"
          : "grid-cols-[208px_1fr] border-b border-[#e5e6e6]",
        className
      )}
    >
      <SideNav compact={compact} activeNavLabel={activeNavLabel} />
      <div className="bg-grey-0 relative flex h-[623px] min-w-0 flex-col overflow-hidden">
        <TopBar />
        {children}
      </div>
    </div>
  );
}

export function ApplicationApp({
  compact,
  headline = "Get approved for financing",
  stepIndex,
}: {
  compact?: boolean;
  headline?: string;
  /** Active onboarding tab — switches main panel content when set. */
  stepIndex?: number;
}) {
  return (
    <AppMockShell compact={compact}>
      {/* Figma content padding — heading at left 94px / top 154px (106px below
            the 48px top bar); card sits at x=554 via the 73px right padding.
            Static showcase (no scroll): the lower form fields fade out behind the
            bottom gradient, exactly as in the Figma mock (5663:23079). */}
      <div className="relative grid grid-cols-[1fr_229px] gap-12 pt-[106px] pr-[73px] pl-[94px]">
        {stepIndex === 1 ? (
          <ConnectPlatformsPlaceholder />
        ) : stepIndex === 2 ? (
          <GetApprovedPlaceholder />
        ) : (
          <BusinessDetailsPanel
            headline={
              stepIndex === 0 ? "Tell us about your business" : headline
            }
          />
        )}

        {/* Right: application + account manager cards */}
        <div className="flex flex-col gap-4">
          <ApplicationCard />
          <AccountManagerCard />
        </div>
      </div>

      {/* Bottom fade + floating actions — Figma scroll-fade (856×190) so the
            form (down to Registered address) fades out instead of hard-clipping. */}
      <div className="from-grey-0/0 to-grey-0 pointer-events-none absolute inset-x-0 bottom-0 h-[190px] bg-gradient-to-b" />
      <div className="absolute right-12 bottom-8 flex items-center gap-2">
        <button
          type="button"
          className="border-grey-200 text-grey-950 rounded-full border bg-white px-4 py-1.5 text-[12px] font-medium"
        >
          Go back
        </button>
        <button
          type="button"
          className="bg-pulse-700 rounded-full px-4 py-1.5 text-[12px] font-medium text-white shadow-[0px_2px_1px_-2px_rgba(0,14,121,0.08)]"
        >
          Continue
        </button>
      </div>
    </AppMockShell>
  );
}

const NAV_GROUPS: {
  heading?: string;
  items: {
    label: string;
    icon: React.ReactNode;
    chevron?: boolean;
  }[];
}[] = [
  {
    items: [
      { label: "Get Started", icon: <AppGetStarted className="size-[18px]" /> },
      { label: "Overview", icon: <AppOverview className="size-[18px]" /> },
    ],
  },
  {
    heading: "Banking",
    items: [
      { label: "Accounts", icon: <AppBank className="size-[18px]" /> },
      {
        label: "Menu Item",
        icon: <AppBank className="size-[18px]" />,
        chevron: true,
      },
      { label: "Cards", icon: <AppCards className="size-[18px]" /> },
      {
        label: "Transactions",
        icon: <AppTransactions className="size-[18px]" />,
      },
    ],
  },
  {
    heading: "Financing",
    items: [
      {
        label: "Financing",
        icon: <AppFinancing className="size-[18px]" />,
        chevron: true,
      },
      {
        label: "Connections",
        icon: <AppConnections className="size-[18px]" />,
      },
      {
        label: "Performance",
        icon: <AppPerformance className="size-[18px]" />,
      },
      { label: "Documents", icon: <AppDocuments className="size-[18px]" /> },
    ],
  },
];

// "Get Started" icon as it appears in the mobile rail (Figma 8987:7120) — a thin
// OUTLINE screen + checkmark + base line, not the filled desktop glyph.
function AppGetStartedLine(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <rect x="1.67" y="1.67" width="16.66" height="14.16" rx="2" />
      <path d="M7.9 8.3 L9.35 9.75 L12.15 6.6" />
      <path d="M5.83 18.33 H14.17" />
    </svg>
  );
}

// "Overview" icon (Figma 8987:7127) — a rounded square holding three ascending
// bars (left short → right tall). Rebuilt 1:1 from the Figma vectors.
function AppOverviewLine(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <rect x="2.5" y="2.5" width="15" height="15" rx="1" />
      <path d="M6.67 13.33V10.83" />
      <path d="M10 13.33V9.17" />
      <path d="M13.33 13.33V6.67" />
    </svg>
  );
}

// "Cash" icon (Figma 8987:7142) — two overlapping banknotes; the front note
// carries a centre coin circle and two side ticks. Exact Figma vectors.
function AppCashLine(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      {/* back note — only the edges outside the front note */}
      <path d="M3.33 13.33H2.5C1.58 13.33 0.83 12.58 0.83 11.66V5C0.83 4.08 1.58 3.33 2.5 3.33H14.16C15.08 3.33 15.83 4.08 15.83 5V6.66" />
      {/* front note */}
      <rect x="3.33" y="6.67" width="15" height="10" rx="1.67" />
      {/* coin circle + side ticks */}
      <circle cx="10.83" cy="11.67" r="1.67" />
      <path d="M15.42 11.66v.01" />
      <path d="M6.25 11.66v.01" />
    </svg>
  );
}

// "Transactions" icon (Figma 8987:7169) — dashed line ending in a down arrow on
// the left, full-height up arrow on the right. Exact Figma vectors.
function AppTransactionsLine(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M14.17 16.67V3.33M11.67 5.83L14.17 3.33L16.67 5.83" />
      <path d="M5.83 3.33V4.17" />
      <path d="M5.83 7.5V8.33" />
      <path d="M5.83 11.67V16.67M3.33 14.17L5.83 16.67L8.33 14.17" />
    </svg>
  );
}

// "Performance" icon (Figma 8987:7216) — stepped ascending columns, one exact
// Figma path (19.5 viewBox padded to keep the 12.5% icon inset).
function AppPerformanceLine(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="-2.44 -2.44 24.38 24.38"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden
      {...props}
    >
      <path d="M6.75 18.75H12.75M6.75 18.75V13.75M6.75 18.75H1.35C1.01863 18.75 0.75 18.4814 0.75 18.15V14.35C0.75 14.0186 1.01863 13.75 1.35 13.75H6.75M12.75 18.75V6.75M12.75 18.75H18.15C18.4814 18.75 18.75 18.4814 18.75 18.15V1.35C18.75 1.01863 18.4814 0.75 18.15 0.75H13.35C13.0186 0.75 12.75 1.01863 12.75 1.35V6.75M12.75 6.75H7.35C7.01863 6.75 6.75 7.01863 6.75 7.35V13.75" />
    </svg>
  );
}

// "Connections" icon (Figma 8987:7224) — a 2-prong power plug (prongs + base +
// cord). iconoir has no matching plug, so it is rebuilt from the Figma vectors.
function AppConnectionsLine(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M7.2 2.9V5.3" />
      <path d="M12.8 2.9V5.3" />
      <rect x="5.83" y="5.3" width="8.34" height="5.66" rx="1" />
      <path d="M10 10.96V17.1" />
    </svg>
  );
}

// Mobile icon rail (Figma 6115:32338) — the fuller sidebar shown in the mobile
// mock: a flat list of icons (top-to-bottom order per the Figma) with the bank as
// the active/selected item and a blue notification dot on "Get Started".
const COMPACT_ICONS: {
  icon: React.ReactNode;
  active?: boolean;
  dot?: boolean;
  dividerAfter?: boolean;
}[] = [
  { icon: <AppGetStartedLine className="size-[18px]" />, dot: true },
  { icon: <AppOverviewLine className="size-[18px]" /> },
  { icon: <Asterisk className="size-[18px]" /> },
  { icon: <AppCashLine className="size-[18px]" /> },
  { icon: <Bank className="size-[18px]" />, active: true },
  { icon: <CreditCards className="size-[18px]" /> },
  { icon: <AppTransactionsLine className="size-[18px]" /> },
  { icon: <Wallet className="size-[18px]" />, dividerAfter: true },
  { icon: <CheckCircle className="size-[18px]" /> },
  { icon: <Book className="size-[18px]" />, dividerAfter: true },
  { icon: <Presentation className="size-[18px]" /> },
  { icon: <AppPerformanceLine className="size-[18px]" />, dividerAfter: true },
  { icon: <AppConnectionsLine className="size-[18px]" /> },
  { icon: <MultiplePages className="size-[18px]" /> },
];

function SideNav({
  compact,
  activeNavLabel,
}: {
  compact?: boolean;
  /** Highlights the matching desktop nav item (e.g. "Financing" on funding screens). */
  activeNavLabel?: string;
}) {
  return (
    <aside className="flex h-[622px] flex-col overflow-hidden border-r border-[#e6e6e5] bg-[#fbfbfb]">
      <div
        className={cn(
          "flex h-12 items-center border-b border-[#e6e6e5] px-4",
          compact && "justify-center px-0"
        )}
      >
        {compact ? (
          // Compact (mobile icon rail): just the "W" mark (Figma 8987:7113),
          // not the full wordmark. Exact Figma vector, 32.706×24 → 18px wide.
          <WayflyerWMark className="h-[13px] w-[18px] shrink-0" />
        ) : (
          <WayflyerWordmark className="h-[14px] w-auto text-[#0d0d0d]" />
        )}
      </div>

      {/* Menu (Figma 5663:23088) — 16.6px padding all sides */}
      {compact ? (
        <nav className="flex flex-1 flex-col items-center gap-1 py-3">
          {COMPACT_ICONS.map((item, i) => (
            <React.Fragment key={i}>
              <span
                className={cn(
                  "relative flex size-6 items-center justify-center rounded-md text-[#737373]",
                  item.active && "bg-[#e4edff] text-[#001eff]"
                )}
              >
                {item.icon}
                {item.dot && (
                  <span className="absolute top-0.5 right-1 size-1.5 rounded-full bg-[#001eff]" />
                )}
              </span>
              {/* Group divider (Figma 8987:7186) — a 4px slot holding a centred
                  item-width hairline (spacing comes from the flex gap). */}
              {item.dividerAfter && (
                <span aria-hidden className="flex h-1 w-6 items-center">
                  <span className="h-px w-full bg-[#e6e6e5]" />
                </span>
              )}
            </React.Fragment>
          ))}
          {/* Secondary section (Figma 8987:7239) — a slightly wider-spaced
              divider, then the Help (?) item. */}
          <span aria-hidden className="mt-1 flex h-1 w-6 items-center">
            <span className="h-px w-full bg-[#e6e6e5]" />
          </span>
          <span className="flex size-6 items-center justify-center text-[#737373]">
            <HelpCircle className="size-[18px]" />
          </span>
        </nav>
      ) : (
        <nav className="flex flex-1 flex-col gap-3 p-4">
          {NAV_GROUPS.map((group, gi) => (
            <div key={group.heading ?? gi} className="flex flex-col gap-0.5">
              {group.heading && (
                <p className="px-2 pt-1 pb-1 text-[10px] text-[#9a9a98]">
                  {group.heading}
                </p>
              )}
              {group.items.map((item) => (
                <NavItem
                  key={item.label}
                  {...item}
                  active={item.label === activeNavLabel}
                  badge={item.label === "Get Started" ? "23%" : undefined}
                />
              ))}
            </div>
          ))}

          {/* Help group — last item in the NavMenu (Figma 10118:4391), spaced
              from Documents by the group gap, not pinned to the bottom. */}
          <div className="pt-[5px]">
            <NavItem label="Help Center" external />
          </div>
        </nav>
      )}

      {/* Footer — desktop (Figma 5663:23090): 16.6px h / 13.9px v padding with a
          0.69px top border. Compact (Figma mobile): just the avatar pinned to the
          bottom, NO top border. */}
      <div
        className={cn(
          "flex items-center gap-2 px-4 py-3.5",
          compact
            ? "justify-center px-0 pt-0 pb-3"
            : "border-t border-[#e6e6e5]"
        )}
      >
        {/* UserMenuTrigger (Figma 5663:23091) — the account avatar (black bowler
            hat on a light-blue #ACC7FF circle), name #bebebe, chevron at right. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/images/home/nav-user-avatar.png"
          alt=""
          width={24}
          height={24}
          className="size-6 shrink-0 object-contain"
        />
        {!compact && (
          <>
            <span className="flex-1 text-[10px] text-[#bebebe]">
              Ghost Golf
            </span>
            <NavArrowRight className="size-3.5 shrink-0 text-[#9a9a98]" />
          </>
        )}
      </div>
    </aside>
  );
}

function NavItem({
  label,
  icon,
  chevron,
  badge,
  external,
  compact,
  active,
}: {
  label: string;
  icon?: React.ReactNode;
  chevron?: boolean;
  badge?: string;
  external?: boolean;
  compact?: boolean;
  active?: boolean;
}) {
  const isHighlighted = Boolean(badge);
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-full px-2 py-1.5 text-[#2f2f2f]",
        compact && "justify-center gap-0 px-0",
        isHighlighted && "border border-[#eaeae9] bg-white",
        active && "bg-[#e4edff] text-[#001eff]"
      )}
    >
      {icon && (
        <span className={cn("text-[#737373]", active && "text-[#001eff]")}>
          {icon}
        </span>
      )}
      {!compact && (
        <>
          <span className="flex-1 text-[11px]">{label}</span>
          {badge && (
            <span className="rounded bg-black/[0.04] px-1 py-0.5 text-[9px] font-medium text-[#737373]">
              {badge}
            </span>
          )}
          {chevron && <NavArrowDown className="size-3 text-[#737373]" />}
          {external && <OpenNewWindow className="size-4 text-[#737373]" />}
        </>
      )}
    </div>
  );
}

// Figma top bar (5854:36914) — white, border-b #e6e6e5. Left: search pill
// (border #cdcccb, "Find anything" #676765, ⌘F keys). Right: three plain icon
// buttons (list, bell, headset), a bordered swap button, then the avatar.
function TopBar() {
  return (
    <div className="flex h-12 shrink-0 items-center justify-between border-b border-[#e6e6e5] px-2.5">
      <div className="flex h-6 w-[394px] max-w-[52%] items-center gap-2 rounded-full border-[0.9px] border-[#cdcccb] px-2.5">
        <Search
          className="size-2.5 shrink-0 text-[#676765]"
          strokeWidth={1.8}
        />
        <span className="flex-1 truncate text-[11px] text-[#676765]">
          Find anything
        </span>
        <span className="flex shrink-0 items-center gap-1 text-[#676765]">
          <kbd className="flex size-[14px] items-center justify-center rounded-[3px] border border-[#e6e6e5] text-[9px]">
            ⌘
          </kbd>
          <kbd className="flex size-[14px] items-center justify-center rounded-[3px] border border-[#e6e6e5] text-[9px]">
            F
          </kbd>
        </span>
      </div>
      <div className="flex items-center gap-2.5">
        <div className="flex items-center gap-1 text-[#676765]">
          <span className="flex size-[19px] items-center justify-center">
            <List className="size-3" strokeWidth={1.8} />
          </span>
          <span className="flex size-[19px] items-center justify-center">
            <Bell className="size-3" strokeWidth={1.8} />
          </span>
          <span className="flex size-[19px] items-center justify-center">
            <Headset className="size-3" strokeWidth={1.8} />
          </span>
        </div>
        <span className="flex size-6 items-center justify-center rounded-full border-[0.9px] border-[#cdcccb] text-[#676765]">
          <SwapArrows className="size-3.5" />
        </span>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={TOPBAR_AVATAR}
          alt=""
          width={24}
          height={24}
          className="size-6 rounded-full object-cover"
        />
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  icon,
  clearable,
  muted,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  clearable?: boolean;
  muted?: boolean;
}) {
  return (
    // Figma Field (5854:36858) — label 9px medium #0d0d0d; input h-26,
    // 0.6px #676765 border, rounded-5, value 10px #0d0d0d.
    <label className="flex flex-col gap-[5px]">
      <span className="text-[9px] leading-none font-medium text-[#0d0d0d]">
        {label}
      </span>
      <span className="flex h-[26px] items-center justify-between gap-[5px] rounded-[5px] border-[0.6px] border-[#676765] bg-white pr-[5px] pl-[10px]">
        <span
          className={cn(
            "flex-1 truncate text-[10px]",
            muted ? "text-[#afafaf]" : "text-[#0d0d0d]"
          )}
        >
          {value}
        </span>
        {clearable && (
          // Figma X clear button (5854:36860) — ~8px icon in a 15.5px button.
          <span className="flex size-4 shrink-0 items-center justify-center">
            <Xmark className="size-[9px] text-[#676765]" strokeWidth={2} />
          </span>
        )}
        {icon && <span className="shrink-0 text-[#676765]">{icon}</span>}
      </span>
    </label>
  );
}

function DataRow({ label, value }: { label: string; value: string }) {
  return (
    // Figma Data row (5854:36863) — stroked green check #33CC5A, label 9px
    // regular #676765, value 9px medium #0d0d0d, 26px row height.
    <div className="flex h-[26px] items-center gap-[5px]">
      <CheckCircle
        className="size-[11px] shrink-0 text-[#33cc5a]"
        strokeWidth={2}
      />
      <div className="flex items-center gap-[3px] text-[9px]">
        <span className="text-[#676765]">{label}</span>
        <span className="truncate font-medium text-[#0d0d0d]">{value}</span>
      </div>
    </div>
  );
}

// Application card (Figma 5854:36934) — a white inner card (header + grid/tile
// illustration) inside a subtle-grey rounded-24 container, with the review
// message as a footer on the grey background.
function ApplicationCard() {
  return (
    <div className="overflow-hidden rounded-[15px] bg-[#f9f9f8]">
      <div className="flex flex-col gap-[15px] rounded-[15px] border border-[#e6e6e5] bg-white py-5 shadow-[0px_4px_1px_-2px_rgba(0,0,0,0.04)]">
        <div className="flex items-start justify-between px-5">
          <span className="text-[12px] font-medium text-[#333333]">
            Application
          </span>
          <span className="rounded bg-[#e4edff] px-1 py-0.5 text-[11px] font-medium text-[#000c66]">
            In progress
          </span>
        </div>

        <div className="relative flex h-[110px] items-center justify-center overflow-hidden px-5">
          <GridBackdrop />
          <div className="relative z-10 flex size-16 items-center justify-center rounded-xl border border-[#1574cd] bg-white">
            <span className="flex size-7 -rotate-[15deg] items-center justify-center rounded-md bg-[#e5f2ff] shadow-[0px_0px_0px_1px_#ffffff,0px_0px_0px_2px_#1574cd]">
              <SnowFlake className="size-4 text-[#1574cd]" />
            </span>
          </div>
        </div>
      </div>

      <div className="flex justify-center px-[15px] pt-2.5 pb-4">
        <p className="max-w-[200px] text-center text-[12px] leading-[1.4] text-[#676765]">
          We&rsquo;ll email you as soon as your review is complete.
        </p>
      </div>
    </div>
  );
}

// Account-manager card (Figma 5854:36935) — titled header row with a divider,
// then avatar + name/email + two icon buttons.
function AccountManagerCard() {
  return (
    <div className="overflow-hidden rounded-[6px] border border-[#e6e6e5] bg-white">
      <div className="border-b border-[#e6e6e5] px-2.5 py-1.5">
        <p className="text-[9px] font-medium text-[#737373]">
          Your account manager
        </p>
      </div>
      <div className="flex items-center gap-2 px-2.5 pt-2 pb-2.5">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={MANAGER_AVATAR}
          alt=""
          width={24}
          height={24}
          className="size-6 shrink-0 rounded-full object-cover"
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-[11px] font-medium text-[#0d0d0d]">
            Sarah Jenkins
          </span>
          <span className="text-pulse-700 truncate text-[9px]">
            sarah.jenkins@wayflyer.com
          </span>
        </div>
        <span className="flex size-5 items-center justify-center rounded-full border-[0.9px] border-[#d9d9d9] bg-white text-[#676765] shadow-[0px_2px_0.5px_-2px_rgba(0,0,0,0.04)]">
          <ChatLines className="size-2.5" strokeWidth={1.8} />
        </span>
        <span className="flex size-5 items-center justify-center rounded-full border-[0.9px] border-[#d9d9d9] bg-white text-[#676765] shadow-[0px_2px_0.5px_-2px_rgba(0,0,0,0.04)]">
          <Calendar className="size-2.5" strokeWidth={1.8} />
        </span>
      </div>
    </div>
  );
}

// Faint grid backdrop behind the Application tile (Figma grid + crosshairs).
function GridBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute inset-x-4 top-1/2 h-px -translate-y-1/2 bg-[#efefef]" />
      <div className="absolute inset-x-10 top-[24%] h-px bg-[#f3f3f3]" />
      <div className="absolute inset-x-10 bottom-[24%] h-px bg-[#f3f3f3]" />
      <div className="absolute inset-y-3 left-[26%] w-px bg-[#f3f3f3]" />
      <div className="absolute inset-y-3 right-[26%] w-px bg-[#f3f3f3]" />
      {[
        "left-[26%] top-[24%]",
        "right-[26%] top-[24%]",
        "left-[26%] bottom-[24%]",
        "right-[26%] bottom-[24%]",
      ].map((pos) => (
        <span
          key={pos}
          className={cn(
            "absolute -translate-x-1/2 -translate-y-1/2 text-[10px] leading-none text-[#cdcccb]",
            pos
          )}
        >
          +
        </span>
      ))}
    </div>
  );
}

// Top bar "Global actions" icon (Figma 5854:36914 / 967:2557) — a horizontal
// swap: top arrow points left, bottom arrow points right.
function SwapArrows({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M20 9H4" />
      <path d="M7 6L4 9L7 12" />
      <path d="M4 15H20" />
      <path d="M17 12L20 15L17 18" />
    </svg>
  );
}

// The standalone Wayflyer "W" mark (Figma 8987:7113) — exact vector, 32.706×24.
function WayflyerWMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32.706 24" fill="none" className={className} aria-hidden>
      <path
        d="M21.9905 0V4.67143H26.2724V4.8L23.1739 16.4571H23.0664L18.9351 3.70714H10.1776V8.4H11.3826V8.55L8.95112 16.4571H8.8005L6.23996 6.85714H0V11.55H2.62509V11.6786L6.13238 24H10.9952L15.8151 10.7143H15.9227L20.9792 24H25.8205L32.706 0H21.9905Z"
        fill="#0D0D0D"
      />
    </svg>
  );
}

function WayflyerWordmark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 147 32" fill="none" className={className} aria-hidden>
      <path
        d="M145.492 6.91591C144.205 6.91591 143.052 7.22723 142.032 7.82765C141.012 8.45031 140.258 9.36205 139.792 10.6074H138.55V7.11605H133.582V11.9861H135.999V24.9062H140.812V15.9222C140.812 14.5879 141.167 13.5872 141.877 12.9646C142.586 12.3197 143.584 11.9861 144.871 11.9861H147V6.91591H145.492Z"
        fill="currentColor"
      />
      <path
        d="M127.927 7.82766C126.596 7.11606 125.021 6.76025 123.158 6.76025C121.295 6.76025 119.61 7.13829 118.212 7.89437C116.837 8.62822 115.773 9.67339 115.019 11.0744C114.265 12.4309 113.887 14.0764 113.887 15.9666C113.887 17.8568 114.265 19.5247 115.041 20.9256C115.817 22.3044 116.904 23.394 118.323 24.1501C119.743 24.9062 121.406 25.2842 123.313 25.2842C125.221 25.2842 127.594 24.7505 129.102 23.6831C130.61 22.5935 131.586 21.1035 131.963 19.2133H127.062C126.795 19.9472 126.374 20.4809 125.753 20.8367C125.132 21.1925 124.311 21.3704 123.269 21.3704C122.227 21.3704 120.874 21.0146 120.075 20.3252C119.277 19.6359 118.767 18.6352 118.567 17.3009H132.03V15.6331C132.03 13.7874 131.675 12.2085 130.965 10.8965C130.255 9.5622 129.235 8.56151 127.882 7.8499L127.927 7.82766ZM118.745 14.032C119.011 12.9201 119.499 12.0751 120.253 11.5191C121.029 10.9409 122.005 10.6518 123.203 10.6518C124.4 10.6518 125.354 10.9409 126.064 11.5191C126.773 12.0751 127.217 12.9201 127.372 14.032H118.745Z"
        fill="currentColor"
      />
      <path
        d="M76.8268 0.978457C76.1171 1.60111 75.7623 2.60181 75.7623 3.93607V7.11605H72.5464V10.9632H75.7179V24.9062H80.6194V10.9632H84.545V7.11605H80.5529V3.84712H84.8999V0H79.821C78.5346 0 77.5366 0.333565 76.8268 0.978457Z"
        fill="currentColor"
      />
      <path
        d="M87.1176 0V20.9701C87.1176 22.3044 87.4724 23.3051 88.1822 23.9277C88.8919 24.5726 89.8899 24.9062 91.1763 24.9062H94.1926V21.0591H92.0412V0H87.1176Z"
        fill="currentColor"
      />
      <path
        d="M61.4128 19.1022H61.3019L56.3338 7.11606H51.1218L58.7735 24.7505L57.3762 28.0639H53.4728V32H57.5315C58.8622 32 59.8602 31.6664 60.5256 31.0215C61.1688 30.3989 61.7454 29.4871 62.2334 28.3308L63.675 24.8839L71.2823 7.09382H66.2477L61.3906 19.0799L61.4128 19.1022Z"
        fill="currentColor"
      />
      <path
        d="M49.2588 13.7206C49.2588 11.4524 48.5491 9.71786 47.1519 8.53927C45.7768 7.36067 43.7585 6.76025 41.1193 6.76025C38.48 6.76025 36.8166 7.27172 35.375 8.27241C33.9334 9.27311 33.024 10.763 32.6248 12.7199H37.5041C37.9255 11.3412 39.1232 10.6518 41.1193 10.6518C43.1153 10.6518 44.557 11.6081 44.557 13.5205V14.2099H39.478C37.1936 14.2099 35.4415 14.6769 34.2217 15.6331C33.0019 16.5893 32.3809 17.9458 32.3809 19.7026C32.3809 21.4594 32.9797 22.7047 34.1552 23.7276C35.3306 24.7283 36.9719 25.2398 39.0566 25.2398C41.1414 25.2398 41.8733 24.9729 42.8936 24.417C43.9138 23.8388 44.7344 23.0382 45.3554 22.0153H46.5974V24.9284H51.5654V20.0584H49.2588V13.7206ZM44.5791 17.7234C44.5791 18.435 44.3795 19.0799 43.9581 19.6581C43.5367 20.214 42.9823 20.6588 42.2726 20.9924C41.5628 21.3259 40.7422 21.5038 39.8107 21.5038C38.8792 21.5038 38.3026 21.3259 37.7925 20.9701C37.3045 20.6143 37.0606 20.1029 37.0606 19.458C37.0606 18.1237 37.9477 17.4788 39.722 17.4788H44.5791V17.7457V17.7234Z"
        fill="currentColor"
      />
      <path
        d="M22.6666 0V4.84781H27.0802V4.98124L23.8864 17.0785H23.7755L19.5172 3.84712H10.4905V8.71716H11.7325V8.87283L9.22633 17.0785H9.07108L6.43182 7.11605H0V11.9861H2.7058V12.1195L6.32092 24.9062H11.3333L16.3013 11.1188H16.4122L21.6242 24.9062H26.6144L33.7116 0H22.6666Z"
        fill="currentColor"
      />
      <path
        d="M105.593 11.9861H106.835V12.1418L104.018 19.1022H103.907L98.939 7.11606H93.727L101.379 24.7505L99.9814 28.0639H96.0779V32H100.137C101.467 32 102.465 31.6664 103.131 31.0215C103.774 30.3989 104.351 29.4871 104.839 28.3308L106.28 24.8839L113.887 7.09382H105.593V11.9861Z"
        fill="currentColor"
      />
    </svg>
  );
}
